<template>
  <div class="app-layout">
    <!-- ========== SIDEBAR ========== -->
    <Sidebar v-model:isOpen="isSidebarOpen" v-model:collapsed="sidebarCollapsed" />

    <!-- ========== MAIN AREA ========== -->
    <div class="app-main" :class="{ 'app-main--sidebar-collapsed': sidebarCollapsed }">
      <!-- ========== NAVBAR ========== -->
      <header class="navbar">
        <div class="navbar-inner">
          <!-- Left: Mobile menu + Page title -->
          <div class="navbar-left">
            <!-- Mobile hamburger -->
            <button
              type="button"
              class="navbar-menu-btn"
              @click="toggleSidebar"
              aria-label="Toggle sidebar"
            >
              <Menu class="h-5 w-5" />
            </button>

            <!-- Page title / Breadcrumb -->
            <div class="navbar-title">
              <h1 class="navbar-title-text">{{ pageTitle }}</h1>
            </div>
          </div>

          <!-- Right: Actions -->
          <div class="navbar-actions">
            <!-- Notification bell -->
            <!-- <button
              type="button"
              class="navbar-icon-btn"
              aria-label="Notifications"
            >
              <Bell class="h-5 w-5" />
              <span class="navbar-badge">3</span>
            </button> -->

            <!-- Theme toggle -->
            <button
              type="button"
              class="navbar-icon-btn"
              @click="toggleTheme"
              :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
            >
              <Sun v-if="isDarkMode" class="h-5 w-5" />
              <Moon v-else class="h-5 w-5" />
            </button>

            <!-- Divider -->
            <div class="navbar-divider" />

            <!-- User dropdown -->
            <div class="navbar-user" ref="userMenuRef">
              <button
                type="button"
                class="navbar-user-btn"
                @click="toggleProfile"
                aria-label="User menu"
              >
                <div class="navbar-user-avatar">
                  {{ userInitials }}
                </div>
                <div class="navbar-user-meta">
                  <span class="navbar-user-name">{{ currentUser?.name || 'User' }}</span>
                  <span class="navbar-user-role">{{ currentUser?.role || 'User' }}</span>
                </div>
                <ChevronDown class="h-4 w-4 navbar-user-chevron" :class="{ 'rotate-180': isProfileOpen }" />
              </button>

              <!-- Dropdown -->
              <Transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 translate-y-1 scale-95"
                enter-to-class="opacity-100 translate-y-0 scale-100"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100 translate-y-0 scale-100"
                leave-to-class="opacity-0 translate-y-1 scale-95"
              >
                <div v-if="isProfileOpen" class="navbar-dropdown">
                  <!-- User info -->
                  <div class="navbar-dropdown-header">
                    <div class="navbar-dropdown-avatar">
                      {{ userInitials }}
                    </div>
                    <div>
                      <p class="navbar-dropdown-name">{{ currentUser?.name || 'User' }}</p>
                      <p class="navbar-dropdown-email">{{ currentUser?.email || 'user@example.com' }}</p>
                    </div>
                  </div>

                  <div class="navbar-dropdown-divider" />

                  <!-- Menu items -->
                  <div class="navbar-dropdown-items">
                    <button v-if="currentUser?.role !== 'admin'" class="navbar-dropdown-item" @click="handleProfileAction('profile')">
                      <User class="h-4 w-4" />
                      <span>My Profile</span>
                    </button>
                    <button class="navbar-dropdown-item" @click="toggleTheme">
                      <Sun v-if="isDarkMode" class="h-4 w-4" />
                      <Moon v-else class="h-4 w-4" />
                      <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
                    </button>
                  </div>

                  <div class="navbar-dropdown-divider" />

                  <!-- Logout -->
                  <div class="navbar-dropdown-items">
                    <button
                      class="navbar-dropdown-item navbar-dropdown-item--danger"
                      @click="handleLogout"
                      :disabled="isLoading"
                    >
                      <LogOut class="h-4 w-4" />
                      <span>{{ isLoading ? 'Signing out...' : 'Sign out' }}</span>
                    </button>
                  </div>

                  <div class="navbar-dropdown-divider" />

                  <!-- Credit -->
                  <div class="navbar-dropdown-credit">
                    Developed by Proweaver Inc.
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </header>

      <!-- ========== MAIN CONTENT ========== -->
      <main class="app-content">
        <div class="app-content-inner">
          <router-view />
        </div>
      </main>

    </div>

    <!-- ========== GLOBAL TOASTS ========== -->
    <BaseToast />

    <!-- ========== PAGE LOADER ========== -->
    <BasePageLoader />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useClickOutside } from '@/composables/ui/useClickOutside'
import { useUser } from '@/composables/auth/useUser'
import { useLogin } from '@/composables/auth/useLogin'
import { useRouter } from 'vue-router'
import Sidebar from './Sidebar.vue'
import { BaseToast, BasePageLoader } from '@/components/ui'
import {
  Menu, Sun, Moon, ChevronDown,
  User, LogOut
} from 'lucide-vue-next'

// Stores & Composables
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const { userInitials, currentUser } = useUser()
const { handleLogout } = useLogin()

// State
const isProfileOpen = ref(false)
const isSidebarOpen = ref(false)
const sidebarCollapsed = ref(false)
const userMenuRef = ref<HTMLElement | null>(null)

// Computed
const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Diasporiques ABA Solutions LLC')
const isLoading = computed(() => authStore.isLoading)
const isDarkMode = computed(() => themeStore.isDarkMode)
const pageTitle = computed(() => (route.meta?.title as string) || 'Dashboard')

// Methods
function toggleProfile() {
  isProfileOpen.value = !isProfileOpen.value
}

function handleProfileAction(action: string) {
  isProfileOpen.value = false
  if (action === 'profile') {
    router.push('/profile')
  }
}

function toggleSidebar() {
  isSidebarOpen.value = !isSidebarOpen.value
}

function toggleTheme() {
  themeStore.toggleTheme()
}

// Click outside to close dropdown
const { handleClickOutside } = useClickOutside(isProfileOpen, userMenuRef)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* ============================================
   LAYOUT
   ============================================ */

.app-layout {
  @apply min-h-screen
         bg-gray-50 dark:bg-gray-900;
}

.app-main {
  @apply transition-all duration-300 ease-in-out;
}

/* Desktop: offset for sidebar */
@media (min-width: 1024px) {
  .app-main {
    margin-left: 16rem; /* w-64 */
  }

  .app-main--sidebar-collapsed {
    margin-left: 4.5rem;
  }
}

/* ============================================
   NAVBAR
   ============================================ */

.navbar {
  @apply fixed top-0 right-0 z-30
         bg-white/80 dark:bg-gray-800/80
         backdrop-blur-md
         border-b border-gray-200 dark:border-gray-700
         shadow-sm dark:shadow-gray-900/50
         transition-all duration-300 ease-in-out;
  left: 0; /* mobile: full width */
}

/* Desktop: offset by sidebar width */
@media (min-width: 1024px) {
  .navbar {
    left: 16rem; /* w-64 */
  }

  .app-main--sidebar-collapsed .navbar {
    left: 4.5rem;
  }
}

.navbar-inner {
  @apply flex items-center justify-between
         h-16
         px-4 sm:px-6;
}

/* Left section */
.navbar-left {
  @apply flex items-center gap-3;
}

.navbar-menu-btn {
  @apply lg:hidden
         p-2 -ml-2
         rounded-xl
         text-gray-500 dark:text-gray-400
         hover:text-gray-700 dark:hover:text-gray-200
         hover:bg-gray-100 dark:hover:bg-gray-700
         transition-colors duration-150;
}

.navbar-title-text {
  @apply text-lg font-semibold
         text-gray-900 dark:text-white
         truncate;
}

/* Actions (right) */
.navbar-actions {
  @apply flex items-center gap-1;
}

.navbar-icon-btn {
  @apply relative p-2
         rounded-xl
         text-gray-500 dark:text-gray-400
         hover:text-gray-700 dark:hover:text-gray-200
         hover:bg-gray-100 dark:hover:bg-gray-700
         transition-colors duration-150;
}

/* Notification badge */
.navbar-badge {
  @apply absolute -top-0.5 -right-0.5
         h-4 min-w-[1rem]
         flex items-center justify-center
         rounded-full
         bg-danger dark:bg-danger-dark
         text-[10px] font-bold text-white
         ring-2 ring-white dark:ring-gray-800;
}

/* Divider */
.navbar-divider {
  @apply h-6 w-px
         bg-gray-200 dark:bg-gray-700
         mx-1;
}

/* User button */
.navbar-user {
  @apply relative;
}

.navbar-user-btn {
  @apply flex items-center gap-2
         px-2 py-1.5
         rounded-xl
         hover:bg-gray-100 dark:hover:bg-gray-700
         transition-colors duration-150;
}

.navbar-user-avatar {
  @apply h-8 w-8
         flex items-center justify-center
         rounded-full
         bg-gradient-to-br from-primary to-primary-hover
         dark:from-primary-dark dark:to-primary-dark
         text-white text-xs font-bold
         shadow-sm shadow-primary/30 dark:shadow-primary-dark/30;
}

.navbar-user-meta {
  @apply hidden sm:flex flex-col items-start;
}

.navbar-user-name {
  @apply text-sm font-medium
         text-gray-900 dark:text-white
         leading-tight;
}

.navbar-user-role {
  @apply text-xs text-gray-500 dark:text-gray-400
         leading-tight;
}

.navbar-user-chevron {
  @apply hidden sm:block
         text-gray-400 dark:text-gray-500
         transition-transform duration-200;
}

/* ============================================
   DROPDOWN
   ============================================ */

.navbar-dropdown {
  @apply absolute right-0 top-full mt-2
         w-64
         bg-white dark:bg-gray-800
         rounded-2xl
         shadow-xl dark:shadow-2xl
         border border-gray-200 dark:border-gray-700
         overflow-hidden
         z-50;
}

.navbar-dropdown-header {
  @apply flex items-center gap-3
         px-4 py-3;
}

.navbar-dropdown-avatar {
  @apply h-10 w-10
         flex items-center justify-center
         rounded-full
         bg-gradient-to-br from-primary to-primary-hover
         dark:from-primary-dark dark:to-primary-dark
         text-white text-sm font-bold
         flex-shrink-0;
}

.navbar-dropdown-name {
  @apply text-sm font-semibold
         text-gray-900 dark:text-white;
}

.navbar-dropdown-email {
  @apply text-xs text-gray-500 dark:text-gray-400
         truncate;
}

.navbar-dropdown-divider {
  @apply border-t border-gray-100 dark:border-gray-700;
}

.navbar-dropdown-items {
  @apply py-1;
}

.navbar-dropdown-item {
  @apply flex items-center gap-3
         w-full px-4 py-2.5
         text-sm text-gray-700 dark:text-gray-300
         hover:bg-gray-50 dark:hover:bg-gray-700/50
         transition-colors duration-100;
}

.navbar-dropdown-item--danger {
  @apply text-danger dark:text-danger-dark
         hover:bg-danger/5 dark:hover:bg-danger-dark/10;
}

/* Credit */
.navbar-dropdown-credit {
  @apply px-4 py-2.5 text-center
         text-[10px] font-medium
         text-gray-400 dark:text-gray-500;
}

/* ============================================
   CONTENT
   ============================================ */

.app-content {
  @apply pt-16; /* offset for fixed navbar */
}

.app-content-inner {
  @apply mx-auto
         px-4 sm:px-6 lg:px-8
         py-6;
}
</style>
 